/*
 * The user's call list, the same on every device (mt-prov-sf docs/calls.md,
 * mt-vox docs/api.md "Shared call list"): the PBX logs every call of the
 * extension (which device rang, which answered); this module syncs the
 * entries. The commands carry the native core's names (core/src/calllog.c):
 *
 *   calllog.list {before?, limit?}  -> {enabled, synced, device, older, entries}
 *                                      newest first; before: unix ms
 *   calllog.more                    fetch the next older page (older)
 *   calllog.delete {entry}          on every device ("entry": its id)
 *   calllog.clear {before?}         every entry up to then (default now)
 *   calllog.sync                    sync now
 *   event calllog.changed {count}
 *
 * GET /v1/config carries calls_seq when the call log is on for the device
 * (features.call_log); GET /v1/calls?since= brings what changed (deleted
 * entries too, which drop). Without calls_seq the page keeps the browser's
 * own history. A browser gets no push: besides the config, it asks every
 * POLL_MS while the list is on. Clips (« Qui était-ce? ») stay on the device
 * that recorded them: the web only says where they are.
 *
 * KV "calllog": {version, seq, device, older, entries}.
 */
import type { Core, CoreModule } from "../core/core";
import { CommandError, Command, Json } from "../core/types";
import { ProvError, type Credential, type ProvClient } from "./client";
import type { KV } from "./store";

const KEY = "calllog";
const VERSION = 1;
const PAGE = 200;
const MAX_CACHED = 2000;
const POLL_MS = 30_000;
const RETRY_MS = 60_000;

export interface CallDevice {
  device: string;
  kind: string; // desktop, android, web, desk, other
  name: string;
}

export interface CallEntry {
  id: string;
  as?: string;
  dir: "in" | "out";
  number: string;
  name?: string;
  did?: string;
  at: number;
  answered_at?: number | null;
  ended_at?: number | null;
  talk_s?: number;
  outcome: string;
  answered_on?: CallDevice | null;
  placed_on?: CallDevice | null;
  answered_by?: { extension: string; name?: string } | null;
  rang?: CallDevice[];
  queue?: string;
  forwarded_to?: string;
  sip_call_ids?: string[];
  clips?: (CallDevice & { clip: string; seconds?: number })[];
  seq?: number;
  deleted?: boolean;
}

interface Page {
  entries: CallEntry[];
  seq: number;
  more?: boolean;
  device?: string;
}

interface Store {
  version: number;
  seq: number;
  device: string;
  older: boolean;
  entries: CallEntry[];
}

const empty = (): Store => ({ version: VERSION, seq: -1, device: "", older: false, entries: [] });

export interface CallLogTimers {
  set(fn: () => void, ms: number): unknown;
  clear(t: unknown): void;
}

const realTimers: CallLogTimers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
};

export class CallLogModule implements CoreModule {
  private store = empty();
  private loaded?: Promise<void>;
  private client?: ProvClient;
  private cred?: Credential;
  private on = false;
  private synced = false;
  private timer?: unknown;
  private busy?: Promise<void>;

  constructor(
    private core: Core,
    private kv: KV,
    private timers: CallLogTimers = realTimers,
  ) {}

  command(c: Command): Promise<Json | void> | undefined {
    if (!c.cmd.startsWith("calllog.")) return undefined;
    return this.run(c).catch((e) => {
      if (e instanceof ProvError) throw new CommandError(e.status === 404 ? "not_found" : e.code, e.message);
      throw e;
    });
  }

  stop(): void {
    if (this.timer !== undefined) this.timers.clear(this.timer);
    this.timer = undefined;
  }

  /** each config: on with calls_seq, off without */
  async onConfig(client: ProvClient, cred: Credential, seq: number | undefined): Promise<void> {
    await this.ready();
    this.client = client;
    this.cred = cred;
    const was = this.on;
    this.on = seq !== undefined;
    if (!this.on) {
      this.stop();
      if (was) this.emit();
      return;
    }
    if (!was) this.emit();
    if (seq !== this.store.seq || !this.synced) await this.sync();
    else this.later(POLL_MS);
  }

  /** the device left provisioning: the list belongs to the user on the server */
  async forget(): Promise<void> {
    await this.ready();
    this.stop();
    this.on = this.synced = false;
    this.client = this.cred = undefined;
    this.store = empty();
    await this.kv.del(KEY);
    this.emit();
  }

  private ready(): Promise<void> {
    this.loaded ??= this.kv.get<Store>(KEY).then((s) => {
      if (s && s.version === VERSION) this.store = s;
    });
    return this.loaded;
  }

  private emit(): void {
    this.core.event("calllog.changed", { count: this.on ? this.store.entries.length : 0 });
  }

  private later(ms: number): void {
    this.stop();
    if (this.on) this.timer = this.timers.set(() => void this.sync(), ms);
  }

  private async save(): Promise<void> {
    this.store.entries.sort((a, b) => b.at - a.at);
    if (this.store.entries.length > MAX_CACHED) {
      this.store.entries.length = MAX_CACHED;
      this.store.older = true;
    }
    await this.kv.set(KEY, this.store);
  }

  private get(path: string): Promise<Page> {
    if (!this.client || !this.cred) throw new CommandError("not_active", "the call list needs an activated device");
    return this.client.call<Page>("GET", path, this.cred);
  }

  /* one sync at a time; a second asks again after it */
  private sync(): Promise<void> {
    this.busy ??= this.fetch().finally(() => (this.busy = undefined));
    return this.busy;
  }

  private async fetch(): Promise<void> {
    if (!this.on) return;
    try {
      if (this.store.seq < 0) {
        // first sync: the newest page, older ones on calllog.more
        const p = await this.get(`/v1/calls?limit=${PAGE}`);
        this.store = { ...empty(), seq: p.seq, device: p.device ?? "", older: !!p.more, entries: p.entries.filter((e) => !e.deleted) };
      } else {
        for (;;) {
          const p = await this.get(`/v1/calls?since=${this.store.seq}&limit=${PAGE}`);
          this.merge(p.entries);
          this.store.seq = Math.max(this.store.seq, p.seq);
          if (p.device) this.store.device = p.device;
          if (!p.more || !p.entries.length) break;
        }
      }
      this.synced = true;
      await this.save();
      this.emit();
      this.later(POLL_MS);
    } catch (e) {
      if (e instanceof ProvError && e.status === 403) {
        // turned off between the config and now
        this.on = false;
        this.emit();
        return;
      }
      this.later(RETRY_MS);
    }
  }

  private merge(changes: CallEntry[]): void {
    const byId = new Map(this.store.entries.map((e) => [e.id, e]));
    for (const e of changes) {
      if (e.deleted) byId.delete(e.id);
      else byId.set(e.id, e);
    }
    this.store.entries = [...byId.values()];
  }

  private async run(c: Command): Promise<Json | void> {
    await this.ready();
    switch (c.cmd) {
      case "calllog.list": {
        const limit = Math.max(1, Math.min(Number(c.limit ?? 100) || 100, MAX_CACHED));
        const before = c.before === undefined ? Infinity : Number(c.before);
        const all = this.on ? this.store.entries.filter((e) => e.at < before) : [];
        return {
          enabled: this.on,
          synced: this.synced,
          device: this.store.device,
          older: this.on && (all.length > limit || this.store.older),
          entries: all.slice(0, limit) as unknown as Json[],
        } as unknown as Json;
      }
      case "calllog.more": {
        if (!this.on) throw new CommandError("off", "the call list is off");
        const oldest = this.store.entries[this.store.entries.length - 1];
        const p = await this.get(`/v1/calls?before=${oldest ? oldest.at : Date.now()}&limit=${PAGE}`);
        this.merge(p.entries.filter((e) => !e.deleted));
        this.store.older = !!p.more;
        await this.save();
        this.emit();
        return;
      }
      case "calllog.delete": {
        const id = String(c.entry ?? "");
        if (!this.store.entries.some((e) => e.id === id)) throw new CommandError("not_found", "no such entry");
        this.store.entries = this.store.entries.filter((e) => e.id !== id);
        await this.save();
        this.emit();
        if (this.client && this.cred) await this.client.call("DELETE", `/v1/calls/${encodeURIComponent(id)}`, this.cred);
        return;
      }
      case "calllog.clear": {
        const before = c.before === undefined ? Date.now() : Number(c.before);
        this.store.entries = this.store.entries.filter((e) => e.at > before);
        if (c.before === undefined) this.store.older = false;
        await this.save();
        this.emit();
        if (this.client && this.cred) await this.client.call("POST", "/v1/calls/clear", this.cred, { before });
        return;
      }
      case "calllog.sync":
        await this.sync();
        return;
    }
    throw new CommandError("unknown_command", c.cmd);
  }
}
